import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Constants, EDIT_OFFER_TABS } from 'app/app/(client)/utils/client-constants'
import { filterSalesforceDiscountLimitsWithRegionAndBranch } from 'app/app/(client)/utils/pricing-summary-utils'
import { ClientProfile, DiscountLimits } from 'app/types/types'

type EditOfferTabType = keyof typeof EDIT_OFFER_TABS
export interface ValidationSection {
  section: EditOfferTabType
  hasErrors: boolean
  errorCount: number
}

export interface OfferingState {
  sections: ValidationSection[]
  totalDiscount: string
  discountLimits: DiscountLimits
  userProfile: ClientProfile
  introduction: string
  additionalDetail: string
  warrantySelection: number | undefined
  customAttachmentComments: string
  customAttachmentSelection: string[]
  attachments: number[]
  initializedOpportunityId: string | null
  isMBSConfigInvalid: boolean
  isApplyDiscount: boolean
}

const initialState: OfferingState = {
  sections: [],
  totalDiscount: Constants.DISCOUNT_DEFAULT_VALUE,
  discountLimits: {} as DiscountLimits,
  userProfile: {} as ClientProfile,
  introduction: '',
  additionalDetail: '',
  warrantySelection: undefined,
  customAttachmentComments: '',
  customAttachmentSelection: [],
  attachments: [],
  initializedOpportunityId: null,
  isMBSConfigInvalid: false,
  isApplyDiscount: false
}

const offeringSlice = createSlice({
  name: 'offering',
  initialState,
  reducers: {
    setIsApplyDiscount: (state, action: PayloadAction<boolean>) => {
      state.isApplyDiscount = action.payload
    },
    markOfferingInitialized: (state, action: PayloadAction<string>) => {
      state.initializedOpportunityId = action.payload
    },
    setMBSConfigInvalid: (state, action: PayloadAction<boolean>) => {
      state.isMBSConfigInvalid = action.payload
    },
    updateSectionValidation: (
      state,
      action: PayloadAction<{
        section: EditOfferTabType
        hasErrors: boolean
        errorCount: number
      }>
    ) => {
      const { section, hasErrors, errorCount } = action.payload
      const existingIndex = state.sections.findIndex(s => s.section === section)

      if (existingIndex >= 0) {
        state.sections[existingIndex] = { section, hasErrors, errorCount }
      } else {
        state.sections.push({ section, hasErrors, errorCount })
      }
    },

    clearValidation: state => {
      state.sections = []
    },
    setTotalDiscount: (state, action: PayloadAction<string>) => {
      state.totalDiscount = action.payload
    },
    setDiscountLimits: (
      state,
      action: PayloadAction<{
        profile: ClientProfile
        discountLimits: DiscountLimits
        introduction: string
        additionalDetail: string
        warrantySelection: number | undefined
        customAttachmentComments: string
        customAttachmentSelection: string[]
        attachments: number[]
      }>
    ) => {
      const {
        profile,
        discountLimits,
        introduction,
        additionalDetail,
        customAttachmentComments,
        customAttachmentSelection,
        attachments,
        warrantySelection
      } = action.payload
      const filterDiscountLimits = filterSalesforceDiscountLimitsWithRegionAndBranch(profile, discountLimits)
      state.discountLimits = filterDiscountLimits
      state.userProfile = profile
      state.introduction = introduction
      state.additionalDetail = additionalDetail
      state.customAttachmentComments = customAttachmentComments
      state.customAttachmentSelection = customAttachmentSelection
      state.attachments = attachments
      state.warrantySelection = warrantySelection
    }
  }
})

export const {
  updateSectionValidation,
  clearValidation,
  setTotalDiscount,
  setDiscountLimits,
  markOfferingInitialized,
  setMBSConfigInvalid,
  setIsApplyDiscount
} = offeringSlice.actions

export default offeringSlice.reducer
